import { formatDate } from "@angular/common";
import { inject, Injectable, LOCALE_ID } from "@angular/core";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { ExpenseType, TripDocumentsDto, TripRequestDto, TripRequestStatus, TripRequestType } from "../../dashboard/models/trip-request-models";
import { OTHER_ADDRESS_CATEGORY } from "../../dashboard/profile/state/profile-model";
import { TripRequestFormFactory } from "./trip-request-form.factory";

@Injectable({
  providedIn: 'root',
})
export class TripRequestFormMapper {
    private locale = inject(LOCALE_ID);
    private tripRequestFormFactory = inject(TripRequestFormFactory);
    
    loadAppointmentData(tripRequest: FormGroup, data: TripRequestDto) {
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
        tripRequest.patchValue({
              tripRequestId: data.tripRequestId,
              tripRequestStatus: data.tripRequestStatusId
        });
        this.loadAppointmentsControls(data, tripAppointments)
    }

    loadAppointmentsControls(data: TripRequestDto, tripAppointments: FormArray ) {
        data.tripAppointments?.forEach((appointment, index) => {
            if (!!tripAppointments.at(index)) {
                tripAppointments.at(index).patchValue( {
                    appointmentId: appointment.appointmentId,
                    appointmentDate: new Date(appointment.appointmentTime),
                    appointmentTime: new Date(appointment.appointmentTime)
                })
            }
            else {
                if (data.tripType === TripRequestType.Pre) {
                    tripAppointments.push(this.tripRequestFormFactory.getPreTripAppointmentFg());
                }
                else {
                    tripAppointments.push(this.tripRequestFormFactory.getPostTripAppointmentFg());
                }
                tripAppointments.at(index).patchValue( {
                    appointmentId: appointment.appointmentId,
                    appointmentDate: new Date(appointment.appointmentTime),
                    appointmentTime: new Date(appointment.appointmentTime)
                })
            }
        });
    }

    loadPreTripRequestData(tripRequest: FormGroup, data: TripRequestDto) {
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
        tripRequest.patchValue({
            tripRequestId: data.tripRequestId,
            tripRequestStatus: TripRequestStatus.Unsubmitted,
            appointmentTypeId: data.appointmentTypeId,
            appointmentType: data.appointmentType,
            isRoundTrip : data.isRoundTrip,
            showAdditionalStop: !data.isRoundTrip,
        });

        if (data.tripAddresses?.length === 2) {
            tripRequest.get('showRoundTrip')?.setValue(true);
        } 
        this.loadAppointmentsControls(data, tripAppointments);
        const tripAddresses = tripRequest.get('tripAddresses') as FormArray;
        this.loadAddressControls(data, tripAddresses);
    }

    loadPostTripRequestData(tripRequest: FormGroup, data: TripRequestDto) {
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
        tripRequest.patchValue({
            tripRequestId: data.tripRequestId,
            tripRequestStatus: TripRequestStatus.Unsubmitted,
            appointmentTypeId: data.appointmentTypeId,
            appointmentType: data.appointmentType,
            isRoundTrip : data.isRoundTrip,
            showAdditionalStop: !data.isRoundTrip,
            isSameDriver: data.isSameDriver,
            driverFirstName: data.driverFirstName,
            driverLastName: data.driverLastName,
            anotherMedicaidConsumer: data.anotherMedicaidConsumer,
            samePassenger: data.samePassenger,
            driverMannualEntry: data.driverMannualEntry,
            driverId: data.driverId,
            isAcceptedPreTrip: data.isAcceptedPreTrip
        });

        if (data.tripAddresses?.length === 2) {
            tripRequest.get('showRoundTrip')?.setValue(true);
        } 
        this.loadAppointmentsControls(data, tripAppointments);

        data.tripAppointments?.forEach((appointment, index) => {
            tripAppointments.at(index).patchValue( {
                driverId: appointment.driverId,
                driverFirstName: appointment.appointmentDriver?.firstName,
                driverLastName: appointment.appointmentDriver?.lastName,
                driverMannualEntry: appointment.driverMannualEntry
            })
        });

        const tripAddresses = tripRequest.get('tripAddresses') as FormArray;
        this.loadAddressControls(data, tripAddresses);
        const tripPassengers = tripRequest.get('tripPassengers') as FormArray;
        this.loadPassengerControls(data,tripPassengers,tripAppointments)
        this.loadTripDocuments(tripRequest, data);
    }

    loadTripDrivers(tripRequest: FormGroup, data: TripRequestDto) {
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
        tripRequest.patchValue({
                tripRequestId: data.tripRequestId,
                tripRequestStatus: data.tripRequestStatusId,
                driverFirstName: data.driverFirstName,
                driverLastName: data.driverLastName
        })

        data.tripAppointments?.forEach((appointment, index) => {
            tripAppointments.at(index).patchValue( {
                driverFirstName: appointment.appointmentDriver?.firstName,
                driverLastName: appointment.appointmentDriver?.lastName,
            })
        });
    }

    loadAddress(tripRequest: FormGroup, data: TripRequestDto) {
        const tripAddresses = tripRequest.get('tripAddresses') as FormArray;
        tripRequest.patchValue({
            tripRequestId: data.tripRequestId,
            tripRequestStatus: data.tripRequestStatusId,
            appointmentTypeId: data.appointmentTypeId,
            appointmentType: data.appointmentType,
            isRoundTrip : data.isRoundTrip
        })
        this.loadAddressControls(data, tripAddresses);
    }

    loadAddressControls(data: TripRequestDto, tripAddresses: FormArray) {
        
        if (!data.tripAddresses || data.tripAddresses?.length <= 0) {
            tripAddresses.push(this.tripRequestFormFactory.getTripAddressFg(1));
            tripAddresses.push(this.tripRequestFormFactory.getTripAddressFg(2));
            return;
        }
        
        data.tripAddresses?.forEach((address, index) => {
            if (!tripAddresses.at(index)) { 
                tripAddresses.push(this.tripRequestFormFactory.getTripAddressFg(address.seqNo));
            }
            tripAddresses.at(index).patchValue( {
                tripAddressId: address.tripAddressId,
                seqNo: address.seqNo,
                providerOrNickName: address.providerOrNickName,
                otherCategoryType: address.otherCategoryType,
                address: address.address,
                locationMannualEntry: (!address.providerOrNickName || address.providerOrNickName.trim() === '') ? false : true,
                isManualAddress: address.isManualAddress,
                isMapAddress: address.isMapAddress
            })

            if (tripAddresses.at(index).get('locationMannualEntry')?.value) {
                tripAddresses.at(index).get('addressCategoryId')?.setValue(address.addressCategoryId);
            }
            else {
                if (Number(address.addressCategoryId) === OTHER_ADDRESS_CATEGORY) {
                    tripAddresses.at(index).get('addressCategoryId')?.setValue(address.otherCategoryType);
                }
                else {
                    tripAddresses.at(index).get('addressCategoryId')?.setValue(address.addressCategoryId);
                }
            }
        });
    }

    loadPassenger(tripRequest: FormGroup, data: TripRequestDto) {
        const tripPassengers = tripRequest.get('tripPassengers') as FormArray;
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
        tripRequest.patchValue({
            tripRequestId: data.tripRequestId,
            tripRequestStatus: data.tripRequestStatusId,
            anotherMedicaidConsumer: data.anotherMedicaidConsumer,
            samePassenger: data.samePassenger
        })
        this.loadPassengerControls(data, tripPassengers, tripAppointments)
    }

    loadPassengerControls(data: TripRequestDto, tripPassengers: FormArray, tripAppointments: FormArray) {
         if (data.anotherMedicaidConsumer) {
            if(data.samePassenger) {
                data.tripPassengers?.forEach((passenger, index) => {
                    if (!tripPassengers.at(index)) { 
                        tripPassengers.push(this.tripRequestFormFactory.getPassengerFg());
                    }
                    tripPassengers.at(index).patchValue({
                        passengerId: passenger.passengerId,
                        firstName: passenger.firstName,
                        lastName: passenger.lastName
                    })
                });
            }
            else {
                data.tripAppointments?.forEach((appointment, i) => {
                    const appointmentPassengers = tripAppointments.at(i).get('appointmentPassengers') as FormArray;
                    appointment.appointmentPassengers?.forEach((passenger, index) => {
                        if (!appointmentPassengers.at(index)) { 
                            appointmentPassengers.push(this.tripRequestFormFactory.getPassengerFg());
                        }
                        appointmentPassengers.at(index).patchValue({
                            passengerId: passenger.passengerId,
                            firstName: passenger.firstName,
                            lastName: passenger.lastName
                        })
                    })
                })
            }
        }
    }

    loadTripDocuments(tripRequest: FormGroup, data: TripRequestDto) {
        const appVerificationDocFg = tripRequest.get('appointmentVerificationDocuments') as FormGroup;
        const tollDocFg = tripRequest.get('tollDocuments') as FormGroup;
        const parkingDocFg = tripRequest.get('parkingDocuments') as FormGroup;
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;

        tripRequest.patchValue({
            tripRequestId: data.tripRequestId,
            tripRequestStatus: data.tripRequestStatusId,
        })

        appVerificationDocFg.patchValue({
            documentUploading: data.appointmentDocumentUploading,
            documentEmailing: data.appointmentDocumentEmailing,
            documentMailing : data.appointmentDocumentMailing,
            documentFaxing: data.appointmentDocumentFaxing,
        })

        tollDocFg.patchValue({
            documentUploading: data.tollDocumentUploading,
            documentEmailing: data.tollDocumentEmailing,
            documentMailing : data.tollDocumentMailing,
            documentFaxing: data.tollDocumentFaxing,
        })

        parkingDocFg.patchValue({
            documentUploading: data.parkingDocumentUploading,
            documentEmailing: data.parkingDocumentEmailing,
            documentMailing : data.parkingDocumentMailing,
            documentFaxing: data.parkingDocumentFaxing,
        })


        data.tripAppointments?.forEach((appointment, i) => {
            if (appointment.tripExpenses) {
                const expenseFr = tripAppointments.at(i).get('tripExpenses') as FormArray;
                const tollExpense = appointment.tripExpenses.find(x => x.expenseType === ExpenseType.Toll);
                expenseFr.at(0).patchValue({
                    expenseId: tollExpense? tollExpense.expenseId : 0,
                    expenseType: tollExpense? tollExpense.expenseType : ExpenseType.Toll,
                    expenseAmount: tollExpense? tollExpense.expenseAmount : '',
                })

                const parkingExpense = appointment.tripExpenses.find(x => x.expenseType === ExpenseType.Parking);
                expenseFr.at(1).patchValue({
                    expenseId: parkingExpense ? parkingExpense.expenseId : 0,
                    expenseType: parkingExpense? parkingExpense.expenseType : ExpenseType.Parking,
                    expenseAmount: parkingExpense? parkingExpense.expenseAmount : ''
                })               
            }
        })
    }

    getDocumentFormData(tripRequest: FormGroup): FormData {

      const documetFormData = new FormData();
      const appVerificationDocFg = tripRequest.get('appointmentVerificationDocuments') as FormGroup;
      const tollDocFg = tripRequest.get('tollDocuments') as FormGroup;
      const parkingDocFg = tripRequest.get('parkingDocuments') as FormGroup;
      const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
      
      documetFormData.append('tripRequestId', tripRequest.get('tripRequestId')?.value);
      documetFormData.append('tripRequestStatus', tripRequest.get('tripRequestStatus')?.value);
      documetFormData.append('tripType', TripRequestType.Post);
      documetFormData.append('stepIndex', '5');
      documetFormData.append('appointmentDocumentUploading', appVerificationDocFg.get('documentUploading')?.value);
      documetFormData.append('appointmentDocumentEmailing', appVerificationDocFg.get('documentEmailing')?.value);
      documetFormData.append('appointmentDocumentMailing', appVerificationDocFg.get('documentMailing')?.value);
      documetFormData.append('appointmentDocumentFaxing', appVerificationDocFg.get('documentFaxing')?.value);
      documetFormData.append('tollDocumentUploading', tollDocFg.get('documentUploading')?.value);
      documetFormData.append('tolldocumentEmailing', tollDocFg.get('documentEmailing')?.value);
      documetFormData.append('tolldocumentMailing', tollDocFg.get('documentMailing')?.value);
      documetFormData.append('tolldocumentFaxing', tollDocFg.get('documentFaxing')?.value);
      documetFormData.append('parkingDocumentUploading', parkingDocFg.get('documentUploading')?.value);
      documetFormData.append('parkingdocumentEmailing', parkingDocFg.get('documentEmailing')?.value);
      documetFormData.append('parkingdocumentMailing', parkingDocFg.get('documentMailing')?.value);
      documetFormData.append('parkingdocumentFaxing', parkingDocFg.get('documentFaxing')?.value);

      tripAppointments.controls.forEach((control: AbstractControl, i) => {
        documetFormData.append(`tripAppointments[${i}].appointmentId`, control.get('appointmentId')?.value);
        documetFormData.append(`tripAppointments[${i}].appointmentDate`, formatDate(control.get('appointmentDate')?.value,'yyyy-MM-dd', this.locale));
        documetFormData.append(`tripAppointments[${i}].appointmentTime`, control.get('appointmentTime')?.value.toISOString());
        
        const expensesFr = control.get('tripExpenses') as FormArray;
        expensesFr.controls.forEach((expenseCtrl: AbstractControl, j) => { 
            documetFormData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseId`, expenseCtrl.get('expenseId')?.value);
            documetFormData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseType`, expenseCtrl.get('expenseType')?.value);
            if (expenseCtrl.get('expenseAmount')?.value) {
              documetFormData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseAmount`, expenseCtrl.get('expenseAmount')?.value);
            }
            documetFormData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseAmount`, '0');
        })
      });
      return documetFormData;
    }

    appendDocumentsData(formData: FormData, documents:TripDocumentsDto[], documentType: string, index: number ) : number {
        documents.forEach((document) => {
            formData.append(`tripDocuments[${index}].documentId`, document.documentId.toString());
            formData.append(`tripDocuments[${index}].fileName`, document.fileName);
            formData.append(`tripDocuments[${index}].path`, document.path);
            formData.append(`tripDocuments[${index}].documentType`, documentType);
            if (document.file) {
                formData.append(`tripDocuments[${index}].file`, document.file);
            }

            if (document.linkDocumentId && documentType === 'Verification') {
                formData.append(`tripDocuments[${index}].linkDocumentId`, document.linkDocumentId.toString());
            }
            index++;
        });
        return index;
    }

    getTripRequestFormData(tripRequest: FormGroup): FormData {

        const formData = new FormData();
        const tripAppointments = tripRequest.get('tripAppointments') as FormArray;
        const tripAddress = tripRequest.get('tripAddresses') as FormArray;

        formData.append('tripRequestId', tripRequest.get('tripRequestId')?.value);
        formData.append('recipientId', Number(tripRequest.get('recipientId')?.value).toString());
        formData.append('tripRequestStatus', tripRequest.get('tripRequestStatus')?.value);
        formData.append('tripType', tripRequest.get('tripType')?.value);
        formData.append('stepIndex', '0');
        formData.append('isSameDriver', tripRequest.get('isSameDriver')?.value);
        formData.append('samePassenger', tripRequest.get('samePassenger')?.value);
        formData.append('appointmentTypeId', tripRequest.get('appointmentTypeId')?.value);
        formData.append('isRoundTrip', tripRequest.get('isRoundTrip')?.value);
        formData.append('consumerAttestation', tripRequest.get('tripAuthorize')?.value);
        formData.append('isSameAgentWillReview', tripRequest.get('isSameAgentWillReview')?.value);

        if (tripRequest.get('tripType')?.value === TripRequestType.Post) {

            formData.append('anotherMedicaidConsumer', tripRequest.get('anotherMedicaidConsumer')?.value);

            if (tripRequest.get('isSameDriver')?.value) {
                formData.append('driverFirstName', tripRequest.get('driverFirstName')?.value);
                formData.append('driverLastName', tripRequest.get('driverLastName')?.value);
                formData.append('driverMannualEntry', tripRequest.get('driverMannualEntry')?.value);
                formData.append('saveDriverIntoProfile', tripRequest.get('saveDriverIntoProfile')?.value);          
            }

            if (tripRequest.get('anotherMedicaidConsumer')?.value && tripRequest.get('samePassenger')?.value) {
                const tripPassengers = tripRequest.get('tripPassengers') as FormArray;
                tripPassengers.controls.forEach((control: AbstractControl, i) => {
                    formData.append(`tripPassengers[${i}].passengerId`, '0');
                    formData.append(`tripPassengers[${i}].firstName`, control.get('firstName')?.value);
                    formData.append(`tripPassengers[${i}].lastName`, control.get('lastName')?.value);        
                });
            }
        }
      
        tripAppointments.controls.forEach((control: AbstractControl, i) => {
            formData.append(`tripAppointments[${i}].appointmentId`, control.get('appointmentId')?.value);
            formData.append(`tripAppointments[${i}].appointmentDate`, formatDate(control.get('appointmentDate')?.value,'yyyy-MM-dd', this.locale));
            formData.append(`tripAppointments[${i}].appointmentTime`, control.get('appointmentTime')?.value.toISOString());
            formData.append(`tripAppointments[${i}].driverMannualEntry`, control.get('driverMannualEntry')?.value);
            formData.append(`tripAppointments[${i}].enteredTotalMiles`, control.get('totalMiles')?.value);
            formData.append(`tripAppointments[${i}].enteredReimbursementAmount`, control.get('totalMileageAmount')?.value);
            formData.append(`tripAppointments[${i}].enteredTotalReimbursementAmount`, control.get('expenseAmount')?.value);
            formData.append(`tripAppointments[${i}].explanationText`, control.get('explanationText')?.value);


            if (!tripRequest.get('isSameDriver')?.value && tripRequest.get('tripType')?.value === TripRequestType.Post ) {
                formData.append(`tripAppointments[${i}].appointmentDriver.id`, '0');
                formData.append(`tripAppointments[${i}].appointmentDriver.firstName`, control.get('driverFirstName')?.value);
                formData.append(`tripAppointments[${i}].appointmentDriver.lastName`, control.get('driverLastName')?.value);
                formData.append(`tripAppointments[${i}].appointmentDriver.funlName`, '');
            }

            if (!tripRequest.get('samePassenger')?.value && tripAppointments.length > 1) {
                const passengerFr = control.get('appointmentPassengers') as FormArray;
                passengerFr.controls.forEach((passengerCtrl: AbstractControl, j) => { 
                    formData.append(`tripAppointments[${i}].appointmentPassengers[${j}].passengerId`, '0');
                    formData.append(`tripAppointments[${i}].appointmentPassengers[${j}].firstName`, passengerCtrl.get('firstName')?.value);
                    formData.append(`tripAppointments[${i}].appointmentPassengers[${j}].lastName`, passengerCtrl.get('lastName')?.value);
                });
            }

            const expensesFr = control.get('tripExpenses') as FormArray;
            expensesFr.controls.forEach((expenseCtrl: AbstractControl, j) => { 
                formData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseId`, expenseCtrl.get('expenseId')?.value);
                formData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseType`, expenseCtrl.get('expenseType')?.value);
                if (expenseCtrl.get('expenseAmount')?.value) {
                    formData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseAmount`, expenseCtrl.get('expenseAmount')?.value);
                }
                formData.append(`tripAppointments[${i}].tripExpenses[${j}].expenseAmount`, '0');
            });

            const legFr = control.get('tripLegs') as FormArray;
            legFr.controls.forEach((legCtrl: AbstractControl, j) => { 
                formData.append(`tripAppointments[${i}].tripLegs[${j}].legId`, '0');
                formData.append(`tripAppointments[${i}].tripLegs[${j}].seqNo`, legCtrl.get('seqNo')?.value);
                formData.append(`tripAppointments[${i}].tripLegs[${j}].fromAddressId`, '0');
                formData.append(`tripAppointments[${i}].tripLegs[${j}].toAddressId`, '0');
                formData.append(`tripAppointments[${i}].tripLegs[${j}].shortestMiles`, legCtrl.get('shortestMiles')?.value);
                if (tripRequest.get('tripType')?.value === TripRequestType.Post) {
                    formData.append(`tripAppointments[${i}].tripLegs[${j}].enteredMiles`, legCtrl.get('enteredMiles')?.value);
                }
            });

        });

        tripAddress.controls.forEach((control: AbstractControl, i) => {
            formData.append(`tripAddresses[${i}].tripAddressId`, '0');
            formData.append(`tripAddresses[${i}].addressCategoryId`, control.get('addressCategoryId')?.value);
            formData.append(`tripAddresses[${i}].seqNo`, control.get('seqNo')?.value);
            formData.append(`tripAddresses[${i}].address`, control.get('address')?.value);
            formData.append(`tripAddresses[${i}].providerOrNickName`, control.get('providerOrNickName')?.value);
            formData.append(`tripAddresses[${i}].otherCategoryType`, control.get('otherCategoryType')?.value);
            formData.append(`tripAddresses[${i}].isManualAddress`, control.get('isManualAddress')?.value); 
            formData.append(`tripAddresses[${i}].saveAddress`, control.get('saveAddress')?.value);  
            formData.append(`tripAddresses[${i}].isMapAddress`, control.get('isMapAddress')?.value);       
        });

      return formData;
    }
}